import React, { useState, useRef, useCallback } from 'react';
import { getApiUrl } from '../utils/apiConfig';

const AdminUpload = () => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const subjectOptions = ['English', 'Science', 'Social Science', 'Computer Science'];

  const MAX_UPLOAD_MB = Number(process.env.REACT_APP_MAX_UPLOAD_MB) || 500;
  const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      if (droppedFile.size > MAX_UPLOAD_BYTES) {
        setError(`File is too large. Maximum size is ${MAX_UPLOAD_MB}MB.`);
        return;
      }
      setFile(droppedFile);
      setError('');
      // Auto-fill title from filename if empty
      if (!title) {
        const name = droppedFile.name.replace('.pdf', '').replace(/[_-]/g, ' ');
        setTitle(capitalizeWords(name));
      }
    } else {
      setError('Please upload a PDF file');
    }
  }, [title, MAX_UPLOAD_BYTES, MAX_UPLOAD_MB]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      if (selectedFile.size > MAX_UPLOAD_BYTES) {
        setError(`File is too large. Maximum size is ${MAX_UPLOAD_MB}MB.`);
        return;
      }
      setFile(selectedFile);
      setError('');
      if (!title) {
        const name = selectedFile.name.replace('.pdf', '').replace(/[_-]/g, ' ');
        setTitle(capitalizeWords(name));
      }
    } else {
      setError('Please upload a PDF file');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUploadResult(null);

    if (!file) {
      setError('Please select a PDF file');
      return;
    }
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!subject) {
      setError('Subject is required');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setProcessing(false);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title.trim());
      formData.append('subject', subject);
      if (gradeLevel.trim()) {
        formData.append('grade_level', gradeLevel.trim());
      }

      const GENERIC_ERROR = 'Unable to complete upload right now. Please try again later.';

      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', getApiUrl('upload/reading-material-file'));
        xhr.timeout = 5 * 60 * 1000; // 5 min — large PDFs + server-side processing

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            // Cap transfer at 90%; the last 10% is unmeasurable server processing.
            const pct = Math.round((event.loaded / event.total) * 90);
            setUploadProgress(pct);
          }
        };

        // Bytes sent — server now processes; switch to the indeterminate phase.
        xhr.upload.onload = () => {
          setUploadProgress(90);
          setProcessing(true);
        };

        xhr.onload = () => {
          // Any 5xx or unreachable (e.g. nginx 502 HTML) → transient server issue.
          if (xhr.status >= 500 || xhr.status === 0) {
            reject(new Error(GENERIC_ERROR));
            return;
          }

          let parsed = null;
          try {
            parsed = JSON.parse(xhr.responseText);
          } catch (e) {
            reject(new Error(GENERIC_ERROR));
            return;
          }

          if (xhr.status >= 200 && xhr.status < 300 && parsed.success) {
            resolve(parsed);
          } else if (xhr.status >= 400 && xhr.status < 500 && (parsed.message || parsed.error)) {
            reject(new Error(parsed.message || parsed.error));
          } else {
            reject(new Error(GENERIC_ERROR));
          }
        };

        xhr.onerror = () => reject(new Error(GENERIC_ERROR));
        xhr.ontimeout = () => reject(new Error(GENERIC_ERROR));
        xhr.send(formData);
      });

      setUploadResult(data);
      // Reset form
      setFile(null);
      setTitle('');
      setSubject('');
      setGradeLevel('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div style={styles.container}>
      {/* Keyframes for the indeterminate processing bar (inline styles can't define @keyframes) */}
      <style>{`
        @keyframes itzIndeterminate {
          0%   { margin-left: -40%; }
          100% { margin-left: 100%; }
        }
      `}</style>
      <div style={styles.card}>
        <h1 style={styles.heading}>Upload Reading Material</h1>
        <p style={styles.subheading}>Upload a PDF textbook for students. It will be processed and available for reading sessions once indexing completes.</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Drag and Drop Zone */}
          <div
            style={{
              ...styles.dropZone,
              ...(dragActive ? styles.dropZoneActive : {}),
              ...(file ? styles.dropZoneWithFile : {}),
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {file ? (
              <div style={styles.fileInfo}>
                <span style={styles.fileIcon}>&#128196;</span>
                <div>
                  <p style={styles.fileName}>{file.name}</p>
                  <p style={styles.fileSize}>{formatFileSize(file.size)}</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  style={styles.removeBtn}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div style={styles.dropPrompt}>
                <span style={styles.uploadIcon}>&#128228;</span>
                <p style={styles.dropText}>
                  {dragActive ? 'Drop your PDF here' : 'Drag & drop a PDF here, or click to browse'}
                </p>
                <p style={styles.dropHint}>Max file size: {MAX_UPLOAD_MB}MB</p>
              </div>
            )}
          </div>

          {/* Metadata Fields */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(capitalizeWords(e.target.value))}
              placeholder="e.g., Earth Science - Grade 7"
              style={styles.input}
              disabled={uploading}
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Subject *</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={styles.input}
              disabled={uploading}
            >
              <option value="">Select a subject</option>
              {subjectOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Grade Level</label>
            <input
              type="text"
              value={gradeLevel}
              onChange={(e) => setGradeLevel(e.target.value)}
              placeholder="e.g., Grade 7, AP, High School"
              style={styles.input}
              disabled={uploading}
            />
          </div>

          {/* Error */}
          {error && <p style={styles.error}>{error}</p>}

          {/* Success */}
          {uploadResult && (
            <div style={styles.success}>
              <p><strong>Upload successful!</strong></p>
              <p>Title: {uploadResult.title}</p>
              <p>Size: {formatFileSize(uploadResult.file_size)}</p>
              <p style={styles.syncNote}>The material is now being indexed in the background. Larger documents can take several minutes (occasionally longer) to appear in student reading lists. You can safely leave this page.</p>
            </div>
          )}

          {/* Upload progress */}
          {uploading && (
            <div style={styles.progressWrap}>
              <div style={styles.progressTrack}>
                {processing ? (
                  <div style={styles.progressBarIndeterminate} />
                ) : (
                  <div style={{ ...styles.progressBar, width: `${uploadProgress}%` }} />
                )}
              </div>
              <p style={styles.progressLabel}>
                {processing
                  ? 'Processing uploaded pages, larger files may take longer'
                  : `Uploading... ${uploadProgress}%`}
              </p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            className="app-btn"
            disabled={uploading || !file || !title || !subject}
            style={{
              ...styles.submitBtn,
              ...(uploading || !file || !title || !subject ? styles.submitBtnDisabled : {}),
            }}
          >
            {uploading ? (processing ? 'Processing…' : `Uploading… ${uploadProgress}%`) : 'Upload Material'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(270deg, #44746d 5%, #283d40 97%)',
    padding: '2rem',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    padding: '3rem',
    maxWidth: '560px',
    width: '100%',
  },
  heading: {
    fontSize: '1.8rem',
    fontWeight: 700,
    color: '#2C5057',
    margin: '0 0 0.5rem 0',
  },
  subheading: {
    fontSize: '0.95rem',
    color: '#555',
    margin: '0 0 2rem 0',
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  dropZone: {
    border: '2px dashed #44746d',
    borderRadius: '12px',
    padding: '2rem',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: 'rgba(68, 116, 109, 0.05)',
  },
  dropZoneActive: {
    border: '2px dashed #2C5057',
    background: 'rgba(68, 116, 109, 0.15)',
  },
  dropZoneWithFile: {
    border: '2px solid #44746d',
    background: 'rgba(68, 116, 109, 0.08)',
  },
  dropPrompt: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
  },
  uploadIcon: {
    fontSize: '2.5rem',
  },
  dropText: {
    fontSize: '1rem',
    color: '#2C5057',
    margin: 0,
    fontWeight: 500,
  },
  dropHint: {
    fontSize: '0.8rem',
    color: '#666',
    margin: 0,
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    justifyContent: 'center',
  },
  fileIcon: {
    fontSize: '2rem',
  },
  fileName: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#2C5057',
    margin: 0,
  },
  fileSize: {
    fontSize: '0.8rem',
    color: '#666',
    margin: 0,
  },
  removeBtn: {
    background: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '0.4rem 0.8rem',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  label: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#2C5057',
  },
  input: {
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    border: '1px solid #44746d',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border 0.2s',
  },
  error: {
    color: '#d32f2f',
    fontSize: '0.9rem',
    margin: 0,
    padding: '0.5rem 1rem',
    background: '#ffebee',
    borderRadius: '8px',
  },
  success: {
    color: '#2C5057',
    fontSize: '0.9rem',
    padding: '1rem',
    background: 'rgba(68, 116, 109, 0.1)',
    borderRadius: '8px',
    lineHeight: 1.6,
    border: '1px solid #44746d',
  },
  syncNote: {
    fontSize: '0.85rem',
    color: '#555',
    fontStyle: 'italic',
    marginTop: '0.5rem',
  },
  submitBtn: {
    padding: '0.9rem',
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#fff',
    background: 'linear-gradient(135deg, #44746d 0%, #2C5057 100%)',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    marginTop: '0.5rem',
    transition: 'opacity 0.2s',
    boxShadow: '0 2px 8px rgba(44, 80, 87, 0.3)',
  },
  submitBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  progressWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  progressTrack: {
    width: '100%',
    height: '10px',
    background: 'rgba(68, 116, 109, 0.15)',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    background: 'linear-gradient(135deg, #44746d 0%, #2C5057 100%)',
    borderRadius: '6px',
    transition: 'width 0.2s ease',
  },
  progressBarIndeterminate: {
    height: '100%',
    width: '40%',
    background: 'linear-gradient(135deg, #44746d 0%, #2C5057 100%)',
    borderRadius: '6px',
    animation: 'itzIndeterminate 1.2s ease-in-out infinite',
  },
  progressLabel: {
    fontSize: '0.85rem',
    color: '#2C5057',
    margin: 0,
    fontWeight: 600,
    textAlign: 'center',
  },
};

export default AdminUpload;
